import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router';
import invariant from 'tiny-invariant';
import { match } from 'ts-pattern';

import type { SubmittedInvoice } from '~/api/invoices/types';

import { useClientsWithTrashed } from '~/api/clients';
import { useInvoice, useStartPaymentFollowUp } from '~/api/invoices';
import { AlertDialog, Card, Container, Dialog, Header, Menu, Redirect, Separator } from '~/components';
import { SearchParamModals } from '~/constants/url';
import { AdvantageSimulationDialog, DocumentLines, isApproved, isNotApproved, isNotPaidYet } from '~/features/documents';
import { useShouldOpenModal } from '~/hooks/useShouldOpenModal';
import { routes } from '~/providers/RouterProvider/router.routes';
import { toast } from '~/utils/toast';

import type { loader } from './InvoiceDetail.loader';

import { ConfirmDownloadFcInvoiceDialog } from '../components/ConfirmDownloadFcInvoiceDialog';
import { ConfirmResendCoInvoiceDialog } from '../components/ConfirmResendCoInvoiceDialog';
import { InvoiceAlreadyApprovedDialog } from '../components/InvoiceAlreadyApprovedDialog';
import { InvoiceConfirmEditDialog } from '../components/InvoiceConfirmEditDialog';
import { InvoicePreviewDialog } from '../components/InvoicePreviewDialog';
import {
  useOnCreditInvoice,
  useOnDownloadFcInvoice,
  useOnDuplicateInvoice,
  useOnEditInvoice,
  useOnResendCoInvoice,
  useOnViewInvoice,
} from '../invoices.hooks';
import { AddFollowUpRemarkDialog } from './components/AddFollowUpRemarkDialog';
import { StartFollowUpDialog } from './components/StartFollowUpDialog';
import { containerStyle, summaryStyle, wrapperStyle } from './InvoiceDetail.css';
import { InvoiceInfo } from './InvoiceInfo';
import { InvoiceSplit } from './InvoiceSplit/InvoiceSplit';
import { InvoiceStatusCard } from './InvoiceStatusCard';
import { InvoiceTimeline } from './InvoiceTimeline';

export const InvoiceDetailGuard = () => {
  const loaderData = useLoaderData<typeof loader>();
  const { data: invoice } = useInvoice(loaderData.id);

  if (!invoice || invoice.status === 'Draft') {
    return <Redirect to={routes.invoices} />;
  }

  return <InvoiceDetail invoice={invoice} />;
};

type Props = {
  invoice: SubmittedInvoice;
};

const InvoiceDetail = ({ invoice }: Props) => {
  const { data: clients } = useClientsWithTrashed();
  const client = clients.find((client) => client.id === invoice.clientId) ?? null;
  invariant(client !== null, `Unexpected missing client ${invoice.clientId} for invoice ${invoice.id}`);

  const { alreadyApprovedHandle, confirmEdit, confirmEditHandle, isPending: isAqcuireLockPending, onEdit } = useOnEditInvoice(invoice);
  const { data: previewData, isPending: isPreviewPending, onPreview, reset: resetPreviewMutation } = useOnViewInvoice(invoice);
  const { isPending: isDuplicatePending, onDuplicate } = useOnDuplicateInvoice(invoice);
  const { confirmDownload, downloadHandle, isPending: isDownloadFcInvoicePending, onDownload } = useOnDownloadFcInvoice(invoice);
  const { confirmResend, isPending: isResendCoInvoicePending, onResend: onResendCoInvoice, resendHandle } = useOnResendCoInvoice(invoice);
  const { onCredit } = useOnCreditInvoice(invoice);

  const canStartPaymentFollowUp =
    invoice.status === 'Expired' && !invoice.hasPaymentFollowUp && dayjs().subtract(3, 'weeks').isAfter(invoice.dueDate);
  const shouldOpenPaymentFollowUpDialog = useShouldOpenModal(SearchParamModals.START_FOLLOW_UP) && canStartPaymentFollowUp;

  const advantageDialogHandle = Dialog.useHandle();
  const startPaymentFollowUpHandle = AlertDialog.useHandle();
  const addPaymentFollowUpRemarkHandle = Dialog.useHandle<number>();
  const startPaymentFollowUpMutation = useStartPaymentFollowUp(invoice.id);

  const { t } = useTranslation(['common', 'invoices']);

  const onStartPaymentFollowUp = () => {
    startPaymentFollowUpMutation.mutate(undefined, {
      onSuccess: (paymentFollowUp) => {
        startPaymentFollowUpHandle.close();
        addPaymentFollowUpRemarkHandle.openWithPayload(paymentFollowUp.id);
      },
      onError: () => toast.danger({ description: t('common:error') }),
    });
  };

  const primaryAction = isNotApproved(invoice) ? 'edit' : canStartPaymentFollowUp ? 'startPaymentFollowUp' : 'preview';
  const isMenuLoading =
    (primaryAction !== 'preview' && isPreviewPending) || isDuplicatePending || isDownloadFcInvoicePending || isResendCoInvoicePending;

  return (
    <>
      <Header.Root>
        <Header.Content backTo={routes.invoices} backToMobileOnly title={invoice.title} />

        <Header.Actions>
          {match(primaryAction)
            .with('edit', () => (
              <Header.Action icon="Edit" loading={isAqcuireLockPending} onClick={onEdit}>
                {t('invoices:overview.rowActions.edit.label')}
              </Header.Action>
            ))
            .with('startPaymentFollowUp', () => (
              <AlertDialog.Trigger
                handle={startPaymentFollowUpHandle}
                render={<Header.Action icon="Gavel">{t('invoices:details.followUp.start')}</Header.Action>}
              />
            ))
            .with('preview', () => (
              <Header.Action icon="Visibility" loading={isPreviewPending} onClick={onPreview}>
                {t('invoices:overview.rowActions.preview')}
              </Header.Action>
            ))
            .exhaustive()}

          <Menu.Root>
            <Menu.Trigger
              render={
                <Header.Action aria-label={t('common:actions.openMenu')} emphasis="tertiary" icon="MoreVert" loading={isMenuLoading} />
              }
            />

            <Menu.List>
              {primaryAction !== 'preview' && (
                <Menu.Item icon="Visibility" onClick={onPreview}>
                  {t('invoices:overview.rowActions.preview')}
                </Menu.Item>
              )}

              <Menu.Item icon="Copy" onClick={onDuplicate}>
                {t('invoices:overview.rowActions.duplicate')}
              </Menu.Item>

              {isApproved(invoice) && (
                <Menu.Item icon="Download" onClick={onDownload}>
                  {t('invoices:overview.rowActions.download')}
                </Menu.Item>
              )}

              {isNotPaidYet(invoice) && invoice.coTransportType !== 'Peppol' && (
                <Menu.Item icon="Send" onClick={onResendCoInvoice}>
                  {t('invoices:overview.rowActions.resendCoInvoice')}
                </Menu.Item>
              )}

              {isNotPaidYet(invoice) && (
                <Menu.Item icon="CreditNote" onClick={onCredit}>
                  {t('invoices:overview.rowActions.credit')}
                </Menu.Item>
              )}

              {isApproved(invoice) && (
                <Dialog.Trigger
                  handle={advantageDialogHandle}
                  render={<Menu.Item icon="Redeem">{t('invoices:overview.rowActions.advantage')}</Menu.Item>}
                />
              )}
            </Menu.List>
          </Menu.Root>
        </Header.Actions>
      </Header.Root>

      <Container className={containerStyle}>
        <div className={wrapperStyle}>
          <div className={summaryStyle}>
            <Card.Group>
              <InvoiceStatusCard invoice={invoice} />

              <InvoiceTimeline invoice={invoice} />
            </Card.Group>

            <InvoiceInfo invoice={invoice} />
          </div>

          <Separator />

          <DocumentLines calculationData={invoice.calculationData} client={client} lines={invoice.invoiceLines} />

          <Separator />

          <InvoiceSplit client={client} invoice={invoice} onDownload={onDownload} onPreview={onPreview} />
        </div>
      </Container>

      <AlertDialog.Root handle={confirmEditHandle}>
        <InvoiceConfirmEditDialog isLocked={invoice.status === 'LockedByUser'} loading={isAqcuireLockPending} onConfirm={confirmEdit} />
      </AlertDialog.Root>

      <AlertDialog.Root handle={alreadyApprovedHandle}>
        <InvoiceAlreadyApprovedDialog invoice={invoice} />
      </AlertDialog.Root>

      <InvoicePreviewDialog html={previewData} onClose={resetPreviewMutation} title={invoice.title} />

      <AlertDialog.Root handle={downloadHandle}>
        <ConfirmDownloadFcInvoiceDialog
          isPeppol={invoice.fcTransportType === 'Peppol'}
          loading={isDownloadFcInvoicePending}
          onConfirm={confirmDownload}
        />
      </AlertDialog.Root>

      <AlertDialog.Root handle={resendHandle}>
        <ConfirmResendCoInvoiceDialog invoice={invoice} loading={isResendCoInvoicePending} onConfirm={confirmResend} />
      </AlertDialog.Root>

      <Dialog.Root handle={advantageDialogHandle}>
        <AdvantageSimulationDialog calculationData={invoice.calculationData} />
      </Dialog.Root>

      <AlertDialog.Root defaultOpen={shouldOpenPaymentFollowUpDialog} handle={startPaymentFollowUpHandle}>
        <StartFollowUpDialog loading={startPaymentFollowUpMutation.isPending} onConfirm={onStartPaymentFollowUp} />
      </AlertDialog.Root>

      <Dialog.Root handle={addPaymentFollowUpRemarkHandle}>
        {({ payload }) =>
          payload && (
            <AddFollowUpRemarkDialog
              invoiceId={invoice.id}
              onClose={() => addPaymentFollowUpRemarkHandle.close()}
              paymentFollowUpId={payload}
            />
          )
        }
      </Dialog.Root>
    </>
  );
};
